import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { VALIDATION_RULES } from '@onboarding-dashboard/shared';
import { useAuth } from '../../contexts/AuthContext.js';
import { apiClient } from '../../services/apiClient.js';

import { VersionBadge } from '../../components/common/VersionBadge.js';
import signupTemplate from './signup.html?raw';
import logoUrl from '../../assets/eXeX_Title_White_with_name.png';
import './signup.css';

interface InviteInfo {
  email: string;
  role: string | null;
}

export function SignupPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const authInitDone = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, registerWithGoogle, isAuthenticated, isLoading, error, clearError } = useAuth();

  if (!isLoading) authInitDone.current = true;

  const inviteToken = searchParams.get('token') ?? '';
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [validatingInvite, setValidatingInvite] = useState(!!inviteToken);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Validate invite token once auth init is done
  useEffect(() => {
    if (!inviteToken || isLoading || isAuthenticated) return;

    const validate = async (): Promise<void> => {
      try {
        const response = await apiClient.get<{
          valid: boolean;
          email: string;
          role: string;
        }>(`/auth/invite/validate?token=${encodeURIComponent(inviteToken)}`);

        if (response.success && response.data && response.data.valid) {
          setInviteInfo({ email: response.data.email, role: response.data.role });
        } else {
          setInviteError(response.error?.message ?? 'Invalid or expired invitation link.');
        }
      } catch {
        setInviteError('Unable to validate invitation. Please try again.');
      } finally {
        setValidatingInvite(false);
      }
    };
    void validate();
  }, [inviteToken, isLoading, isAuthenticated]);

  // Update DOM based on validation state
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const loadingDiv = container.querySelector<HTMLDivElement>('#signup-loading');
    const externalForm = container.querySelector<HTMLFormElement>('#external-form');
    const googleContainer = container.querySelector<HTMLDivElement>('#google-signup-container');
    const inviteInfoDiv = container.querySelector<HTMLDivElement>('#signup-invite-info');

    if (validatingInvite) {
      if (loadingDiv) loadingDiv.style.display = 'block';
      if (externalForm) externalForm.style.display = 'none';
      if (googleContainer) googleContainer.style.display = 'none';
      if (inviteInfoDiv) inviteInfoDiv.style.display = 'none';
      return;
    }

    if (loadingDiv) loadingDiv.style.display = 'none';

    if (inviteError) {
      if (externalForm) externalForm.style.display = 'none';
      if (googleContainer) googleContainer.style.display = 'none';
      if (inviteInfoDiv) inviteInfoDiv.style.display = 'none';
      return;
    }

    if (inviteInfo) {
      // Show invite info
      if (inviteInfoDiv) {
        inviteInfoDiv.style.display = 'block';
        const emailEl = container.querySelector<HTMLElement>('#signup-invite-email');
        const roleEl = container.querySelector<HTMLElement>('#signup-invite-role');
        if (emailEl) emailEl.textContent = inviteInfo.email;
        if (roleEl) roleEl.textContent = inviteInfo.role ?? 'MC Access';
      }

      const isExternal = inviteInfo.role === 'external';
      if (externalForm) externalForm.style.display = isExternal ? 'flex' : 'none';
      if (googleContainer) googleContainer.style.display = isExternal ? 'none' : 'flex';

      // Pre-fill email for external form
      if (isExternal) {
        const emailInput = container.querySelector<HTMLInputElement>('#signup-email');
        if (emailInput) emailInput.value = inviteInfo.email;
      }
    }
  }, [validatingInvite, inviteError, inviteInfo]);

  // Display errors
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const errorDiv = container.querySelector<HTMLDivElement>('#signup-error');
    const errorMsg = container.querySelector<HTMLSpanElement>('#signup-error-message');
    if (errorDiv && errorMsg) {
      const activeError = error ?? inviteError;
      if (activeError) {
        errorMsg.textContent = activeError;
        errorDiv.style.display = 'block';
      } else {
        errorDiv.style.display = 'none';
        errorMsg.textContent = '';
      }
    }
  }, [error, inviteError]);

  // Toggle loading state on submit button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const submitBtn = container.querySelector<HTMLButtonElement>('#signup-submit');
    if (submitBtn) {
      submitBtn.disabled = isLoading;
      submitBtn.textContent = isLoading ? 'Creating account...' : 'Create Account';
    }
  }, [isLoading]);

  // Form validation for external account creation
  const validateForm = useCallback((): boolean => {
    const container = containerRef.current;
    if (!container) return false;
    let isValid = true;

    const fields = ['first-name', 'last-name', 'email', 'password', 'confirm-password'];
    for (const field of fields) {
      const errorEl = container.querySelector<HTMLSpanElement>(`#signup-${field}-error`);
      const inputEl = container.querySelector<HTMLInputElement>(`#signup-${field}`);
      if (errorEl) errorEl.textContent = '';
      inputEl?.classList.remove('input-error');
    }

    const firstName = (
      container.querySelector<HTMLInputElement>('#signup-first-name')?.value ?? ''
    ).trim();
    const lastName = (
      container.querySelector<HTMLInputElement>('#signup-last-name')?.value ?? ''
    ).trim();
    const email = (container.querySelector<HTMLInputElement>('#signup-email')?.value ?? '').trim();
    const password = container.querySelector<HTMLInputElement>('#signup-password')?.value ?? '';
    const confirmPassword =
      container.querySelector<HTMLInputElement>('#signup-confirm-password')?.value ?? '';

    if (!firstName) {
      setFieldError(container, 'signup-first-name', 'First name is required');
      isValid = false;
    }
    if (!lastName) {
      setFieldError(container, 'signup-last-name', 'Last name is required');
      isValid = false;
    }
    if (!email || !VALIDATION_RULES.EMAIL_REGEX.test(email)) {
      setFieldError(container, 'signup-email', 'A valid email is required');
      isValid = false;
    }
    if (!password || password.length < VALIDATION_RULES.MIN_PASSWORD_LENGTH) {
      setFieldError(
        container,
        'signup-password',
        `Password must be at least ${VALIDATION_RULES.MIN_PASSWORD_LENGTH} characters`
      );
      isValid = false;
    } else if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      setFieldError(container, 'signup-password', 'Must contain uppercase, lowercase, and a digit');
      isValid = false;
    }
    if (password !== confirmPassword) {
      setFieldError(container, 'signup-confirm-password', 'Passwords do not match');
      isValid = false;
    }

    return isValid;
  }, []);

  // Set logo, attach form handler
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const logoEl = container.querySelector<HTMLImageElement>('#signup-logo');
    if (logoEl) logoEl.src = logoUrl;

    // External form handler
    const formEl = container.querySelector<HTMLFormElement>('#external-form');
    const handleSubmit = (e: Event): void => {
      e.preventDefault();
      clearError();
      if (!validateForm()) return;

      const firstName = (
        container.querySelector<HTMLInputElement>('#signup-first-name')?.value ?? ''
      ).trim();
      const lastName = (
        container.querySelector<HTMLInputElement>('#signup-last-name')?.value ?? ''
      ).trim();
      const email = (
        container.querySelector<HTMLInputElement>('#signup-email')?.value ?? ''
      ).trim();
      const password = container.querySelector<HTMLInputElement>('#signup-password')?.value ?? '';
      const confirmPassword =
        container.querySelector<HTMLInputElement>('#signup-confirm-password')?.value ?? '';

      void register({
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
        invitationToken: inviteToken,
      });
    };
    formEl?.addEventListener('submit', handleSubmit);

    // Sign-in link handler
    const signinLink = container.querySelector<HTMLAnchorElement>('#signin-link');
    const handleSigninClick = (e: Event): void => {
      e.preventDefault();
      window.location.href = '/login';
    };
    signinLink?.addEventListener('click', handleSigninClick);

    return () => {
      formEl?.removeEventListener('submit', handleSubmit);
      signinLink?.removeEventListener('click', handleSigninClick);
    };
  }, [register, clearError, validateForm, inviteToken, isLoading]);

  // Render Google sign-in button for internal users
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !inviteInfo || inviteInfo.role === 'external') return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;
    const renderGoogleSignin = (): void => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        const googleContainer = container.querySelector<HTMLDivElement>(
          '#google-signup-button-container'
        );
        if (googleContainer && window.google?.accounts?.id) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (response: { credential: string }) => {
              void registerWithGoogle(response.credential, inviteToken);
            },
          });
          window.google.accounts.id.renderButton(googleContainer, {
            theme: 'outline',
            size: 'large',
            width: googleContainer.offsetWidth || undefined,
            text: 'signin_with',
          });
        }
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleSignin();
    } else if (window.__gsiReady) {
      void window.__gsiReady.then(renderGoogleSignin);
    }

    return () => {
      cancelled = true;
    };
  }, [registerWithGoogle, inviteInfo, inviteToken, isLoading]);

  // No token → redirect to login
  if (!inviteToken) {
    return <Navigate to="/login" replace />;
  }

  if (!authInitDone.current) return <div />;

  return (
    <>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: signupTemplate }} />
      <VersionBadge />
    </>
  );
}

function setFieldError(container: HTMLDivElement, field: string, message: string): void {
  const errorEl = container.querySelector<HTMLSpanElement>(`#${field}-error`);
  const inputEl = container.querySelector<HTMLInputElement>(`#${field}`);
  if (errorEl) errorEl.textContent = message;
  inputEl?.classList.add('input-error');
}
